import React from "react";
import { buildEmptyRow, getByPath, setByPath, type PreviewValues } from "../opportunityFormCommon";
import type { TemplateFieldLike, TemplateStepLike } from "../opportunityFormTypes";

type TranslateFn = (key: string) => string;
type TranslateWithOptionsFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

type RepeatableStepProps = {
  step: TemplateStepLike;
  previewValues: PreviewValues;
  setPreviewValues: React.Dispatch<React.SetStateAction<PreviewValues>>;
  renderFieldWithLabel: (
    field: TemplateFieldLike,
    fullPath: string,
    parentPath: string,
  ) => React.ReactNode;
  isErrorShown: (fullPath: string) => boolean;
  isFieldValueValid: (field: TemplateFieldLike, fullPath: string) => boolean;
  isFieldVisible: (field: TemplateFieldLike, parentPath: string) => boolean;
  resetAttemptedForCurrentStep: () => void;
  clearTouchedUnder: (prefix: string) => void;
  translateFieldText: TranslateFn;
  t: TranslateWithOptionsFn;
};

export const RepeatableStep: React.FC<RepeatableStepProps> = ({
  step,
  previewValues,
  setPreviewValues,
  renderFieldWithLabel,
  isErrorShown,
  isFieldValueValid,
  isFieldVisible,
  resetAttemptedForCurrentStep,
  clearTouchedUnder,
  translateFieldText,
  t,
}) => {
  const stepPath = step.stepId;
  const arr = getByPath<unknown[]>(previewValues, stepPath) || [];
  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-2">
        <button
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-[#0080FF] bg-[#F4F4F5] hover:bg-[#E4E4E7] transition"
          onClick={() => {
            const next = JSON.parse(JSON.stringify(previewValues || {}));
            const stepArr = getByPath<unknown[]>(next, stepPath) || [];
            stepArr.push(buildEmptyRow(step.fields));
            setByPath(next, stepPath, stepArr);
            setPreviewValues(next);
            resetAttemptedForCurrentStep();
          }}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          {t("add")}
        </button>
        {arr.length > 0 && (
          <button
            className="px-4 py-2 rounded-full text-sm font-medium text-[#B91C1C] bg-[#FEE2E2] hover:bg-[#FECACA] transition"
            onClick={() => {
              const next = JSON.parse(JSON.stringify(previewValues || {}));
              setByPath(next, stepPath, []);
              setPreviewValues(next);
              clearTouchedUnder(`${stepPath}[`);
            }}
          >
            {t("clearAll")}
          </button>
        )}
      </div>

      {arr.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/40 text-gray-400 gap-2">
          <svg
            className="w-8 h-8 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-[13px]">{t("noItemsAddedYet")}</p>
        </div>
      )}

      {arr.map((item: unknown, index: number) => {
        const itemHasError = (step.fields || []).some((field) => {
          const fieldPath = `${stepPath}[${index}].${field.key}`;
          return isErrorShown(fieldPath) && !isFieldValueValid(field, fieldPath);
        });
        return (
          <div
            key={index}
            className={`border rounded-3xl p-5 transition-all ${itemHasError
                ? "border-red-200 bg-red-50/20"
                : "border-[#E4E4E7] bg-white shadow-sm"
              }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="size-6 rounded-full bg-[#0080FF] text-white flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <span className="text-sm font-bold text-[#11181C]">
                  {translateFieldText(step.title)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = JSON.parse(
                    JSON.stringify(previewValues || {}),
                  );
                  const stepArr = getByPath<unknown[]>(next, stepPath) || [];
                  stepArr.splice(index, 1);
                  setByPath(next, stepPath, stepArr);
                  setPreviewValues(next);
                  clearTouchedUnder(`${stepPath}[`);
                }}
                className="size-7 flex items-center justify-center rounded-full bg-[#F4F4F5] text-[#71717A] hover:bg-[#E4E4E7] transition"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                {(step.fields || []).map((field) => {
                  const fieldPath = `${stepPath}[${index}].${field.key}`;
                  if (!isFieldVisible(field, `${stepPath}[${index}]`))
                    return null;
                  return (
                    <div key={field.key}>
                      {renderFieldWithLabel(
                        field,
                        fieldPath,
                        `${stepPath}[${index}]`,
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
